import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Users, Eye, TrendingUp, MapPin, PlayCircle, Video } from 'lucide-react';
import { performanceMetrics } from '../data/mock';

const iconMap = {
  Users,
  Eye,
  TrendingUp,
  MapPin,
  PlayCircle,
  Video
};

// Counter animation hook
const useCountUp = (end, isVisible, duration = 2000) => {
  const [count, setCount] = useState('0');
  const endValue = parseFloat(end.replace(/[^0-9.]/g, ''));
  const suffix = end.replace(/[0-9.]/g, '');

  useEffect(() => {
    if (!isVisible) return;

    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const currentValue = progress * endValue;
      
      if (endValue >= 1) {
        setCount(currentValue.toFixed(currentValue < 10 ? 1 : 0) + suffix);
      } else {
        setCount(currentValue.toFixed(1) + suffix);
      }
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };
    requestAnimationFrame(animate);
  }, [end, endValue, suffix, isVisible, duration]);

  return count;
};

const MetricCard = ({ metric, index, isVisible }) => {
  const IconComponent = iconMap[metric.icon];
  const animatedValue = useCountUp(metric.value, isVisible);

  return (
    <Card
      className={`bg-[#111111] border-white/5 rounded-2xl p-6 transition-all duration-500 hover:-translate-y-2 hover:border-[#FF3B3B]/20 hover:shadow-xl hover:shadow-[#FF3B3B]/5 group ${
        isVisible ? 'animate-fadeInUp' : 'opacity-0'
      }`}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 rounded-xl bg-[#FF3B3B]/10 flex items-center justify-center group-hover:bg-[#FF3B3B]/20 transition-colors duration-300">
          <IconComponent className="text-[#FF3B3B]" size={24} />
        </div>
        <div className="w-2 h-2 rounded-full bg-[#FF3B3B] animate-pulse" />
      </div>
      <div className="text-3xl md:text-4xl font-bold text-white mb-2">
        {animatedValue}
      </div>
      <p className="text-[#9CA3AF] text-sm">{metric.label}</p>
    </Card>
  );
};

const PerformanceMetrics = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-[#0B0B0B] py-24" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#FF3B3B] text-sm font-semibold uppercase tracking-widest mb-4 block">
            Performance Metrics
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Numbers That Drive Results
          </h2>
          <p className="text-[#9CA3AF] text-lg max-w-2xl mx-auto">
            Real-time metrics from my social platforms that translate to real ROI for your brand.
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {performanceMetrics.map((metric, index) => (
            <MetricCard
              key={metric.label}
              metric={metric}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PerformanceMetrics;
